
import {Component, Prop, Vue} from "vue-property-decorator";
import {Getter} from "vuex-class";
import { formRules } from "@/utils/formRules";
import { NON_TRANSFERABLE_PROVIDER, NON_TRANSFERABLE_TYPE } from "@/utils/nonTransferables";
import QrCodeScannerWrapper from "@/components/qrcode/QrCodeScannerWrapper.vue";

@Component({
  components: { QrCodeScannerWrapper },
})
export default class CreateNonTransferable extends Vue {

    @Prop({default: false})
    public dialog: boolean;

    @Prop({default: null})
    public building: any;

    @Getter private isAdmin: boolean;
    @Getter private isMobile: boolean;

    get nonTransferableProviders(): any[] {
      return Object.keys(NON_TRANSFERABLE_PROVIDER)
        // Only admin user can create Urmet devices
        .filter((provider) => provider !== NON_TRANSFERABLE_PROVIDER.URMET
          || (this.isAdmin && this.building?.urmetSitId))
        .map((provider) => ({
          key: provider, label: this.$t(`NonTransferable.providers.${provider}`),
        }));
    }

    private NON_TRANSFERABLE_PROVIDER = NON_TRANSFERABLE_PROVIDER;
    private nonTransferableTypes = Object.keys(NON_TRANSFERABLE_TYPE).map((type) => ({
      key: type, label: this.$t(`NonTransferable.types.${type}`),
    }));
    private nonTransferableData: {
      name: string,
      type: string,
      provider: string,
      externalId: string,
      activationCode?: string,
    } = {
      name: null,
      type: this.nonTransferableTypes[0].key,
      provider: this.nonTransferableProviders[0].key,
      externalId: null,
      activationCode: null,
    };
    private valid: boolean = true;
    private loading: boolean = false;
    private rules = formRules;
    private loadingQrCodeStreamer: boolean = false;
    private hasScannedOnboardingQrCode: boolean = false;

    get displayQrCodeStreamer(): boolean {
      // Only display QR Code streamer when creating a new Masterlock device
      return this.nonTransferableData.provider === NON_TRANSFERABLE_PROVIDER.MASTERLOCK
        && !this.hasScannedOnboardingQrCode;
    }

    private onDecode(decodedString: string): void {
      if (decodedString) {
        this.loadingQrCodeStreamer = true;
        this.$nextTick().then(() => {
          const [externalId, activationCode] = decodedString.split(":");
          if (!externalId || !activationCode) {
            // If stream didn't work, display error message and re-activate it
            this.$store.commit("alerts/displayError", {
              msg: this.$i18n?.t("connectedBoxOnboarding.unknownQrCode"),
            });
          } else {
            // Else, automatically fill connected box data
            this.nonTransferableData.externalId = externalId;
            this.nonTransferableData.activationCode = activationCode;
            this.hasScannedOnboardingQrCode = true;
          }
          this.loadingQrCodeStreamer = false;
        });
      }
    }

    private close(): void {
        this.$emit("close");
        // Reset nonTransferableData (except provider to prevent QR code scanner issues)
        this.nonTransferableData.name = null;
        this.nonTransferableData.type = this.nonTransferableTypes[0].key;
        this.nonTransferableData.externalId = null;
        this.nonTransferableData.activationCode = null;
        this.hasScannedOnboardingQrCode = false;
    }

    private async createNonTransferable(): Promise<void> {
        this.loading = true;
        await this.$store.dispatch("buildings/createNonTransferable", {
            buildingId: this.building.id,
            nonTransferableData: {
              ...this.nonTransferableData,
              buildingId: this.building.id,
            },
        }).then((res) => {
            this.$store.commit("alerts/displaySuccess", {
                msg: this.$t("NonTransferable.added"),
            });
            this.$emit("success");
            this.close();
        }).catch((error) => {
            this.$store.commit("alerts/displayHttpError", { error });
        }).finally(() => {
            this.loading = false;
        });
    }
}
